import React, { useEffect, useState } from "react";
import { AUTH_REQUIRED_EVENT, beginLogin, readSignOutReason } from "../utils/auth";

/**
 * Reasons the backend can hand back on ?auth_error=. Deliberately coarse — the
 * detail stays in the server log rather than in a URL the user can see.
 */
const MESSAGES: Record<string, string> = {
  expired_login: "That sign-in link had expired. Please try again.",
  exchange_failed: "Sign-in could not be completed. Please try again.",
  access_denied: "Sign-in was declined.",
};

/** How long a recorded sign-out stays worth showing next to the retry. */
const RECENT_SIGN_OUT_MS = 60 * 60 * 1000;

const AuthErrorBanner: React.FC = () => {
  // A sign-in the user backed out of leaves no trace in the URL: the app only
  // learns about it when the next request is refused and no new sign-in can be
  // started. Without this the app just keeps reporting an expired session.
  const [interrupted, setInterrupted] = useState(false);

  useEffect(() => {
    const handleAuthRequired = (): void => setInterrupted(true);
    window.addEventListener(AUTH_REQUIRED_EVENT, handleAuthRequired);
    return () => window.removeEventListener(AUTH_REQUIRED_EVENT, handleAuthRequired);
  }, []);

  const reason = new URLSearchParams(window.location.search).get("auth_error");
  if (!reason && !interrupted) return null;

  const message = reason
    ? (MESSAGES[reason] ?? "Sign-in failed. Please try again.")
    : "Sign-in was not completed. Please sign in again.";

  // A sign-out on the native app happens silently while nobody is watching, so
  // the app carries its own account of it here rather than leaving the cause to
  // be reconstructed from provider logs a day later.
  const signedOut = readSignOutReason();
  const explanation =
    signedOut && Date.now() - signedOut.at < RECENT_SIGN_OUT_MS
      ? `The app signed out because ${signedOut.reason}.`
      : undefined;

  return (
    <section className="app-section" role="alert">
      <div className="app-card error-container">
        <p className="error-message">{message}</p>
        {explanation && <p className="error-detail">{explanation}</p>}
        <button
          type="button"
          className="app-button secondary-button"
          onClick={() => {
            setInterrupted(false);
            void beginLogin("/", { force: true });
          }}
        >
          Sign in again
        </button>
      </div>
    </section>
  );
};

export default AuthErrorBanner;
